/* ------------------------------------------------------------------ */
/* UK Stamp Duty Land Tax (SDLT) — England & Northern Ireland          */
/* Last updated: February 2026                                         */
/* Source: https://www.gov.uk/stamp-duty-land-tax/residential-property-rates */
/* ------------------------------------------------------------------ */

export type BuyerType = "home-mover" | "first-time" | "additional" | "non-uk"

export interface StampDutyBand {
  from: number
  to: number | null
  rate: number
}

export interface StampDutyResult {
  totalDuty: number
  effectiveRate: number
  bands: { from: number; to: number; rate: number; duty: number }[]
  propertyPrice: number
  buyerType: BuyerType
}

/* ── Standard SDLT bands (home movers) ──────────────────────────── */
const STANDARD_BANDS: StampDutyBand[] = [
  { from: 0, to: 250_000, rate: 0 },
  { from: 250_000, to: 925_000, rate: 5 },
  { from: 925_000, to: 1_500_000, rate: 10 },
  { from: 1_500_000, to: null, rate: 12 },
]

/* ── First-time buyer bands ──────────────────────────────────────── */
/* Relief available on properties up to £625,000 only                  */
const FIRST_TIME_BANDS: StampDutyBand[] = [
  { from: 0, to: 425_000, rate: 0 },
  { from: 425_000, to: 625_000, rate: 5 },
]

const FIRST_TIME_THRESHOLD = 625_000

/* ── Surcharges ──────────────────────────────────────────────────── */
const ADDITIONAL_PROPERTY_SURCHARGE = 5 // +5% on all bands (from Oct 2024)
const NON_UK_RESIDENT_SURCHARGE = 2 // +2% on all bands

/* ── Defaults ────────────────────────────────────────────────────── */
export const STAMP_DUTY_DEFAULTS = {
  propertyPrice: 350_000,
  buyerType: "home-mover" as BuyerType,
} as const

export const BUYER_TYPE_OPTIONS: { value: BuyerType; label: string; description: string }[] = [
  {
    value: "home-mover",
    label: "Home mover",
    description: "Buying a replacement main residence",
  },
  {
    value: "first-time",
    label: "First-time buyer",
    description: "Never owned a property before",
  },
  {
    value: "additional",
    label: "Additional property",
    description: "Buy-to-let or second home (+5%)",
  },
  {
    value: "non-uk",
    label: "Non-UK resident",
    description: "Not UK resident (+2% surcharge)",
  },
]

/* ── Calculate ───────────────────────────────────────────────────── */
export function calculateStampDuty(
  propertyPrice: number,
  buyerType: BuyerType
): StampDutyResult {
  if (propertyPrice <= 0) {
    return {
      totalDuty: 0,
      effectiveRate: 0,
      bands: [],
      propertyPrice,
      buyerType,
    }
  }

  // Determine base bands
  let baseBands: StampDutyBand[]
  if (
    buyerType === "first-time" &&
    propertyPrice <= FIRST_TIME_THRESHOLD
  ) {
    baseBands = FIRST_TIME_BANDS
  } else {
    // First-time buyers over threshold pay standard rates
    baseBands = STANDARD_BANDS
  }

  // Calculate surcharge
  let surcharge = 0
  if (buyerType === "additional") surcharge = ADDITIONAL_PROPERTY_SURCHARGE
  if (buyerType === "non-uk") surcharge = NON_UK_RESIDENT_SURCHARGE

  // Calculate duty per band
  const bands: StampDutyResult["bands"] = []
  let totalDuty = 0

  for (const band of baseBands) {
    const bandTop = band.to ?? Infinity
    if (propertyPrice <= band.from) break

    const taxableAmount =
      Math.min(propertyPrice, bandTop) - band.from
    const effectiveRate = band.rate + surcharge
    const duty = (taxableAmount * effectiveRate) / 100

    bands.push({
      from: band.from,
      to: Math.min(propertyPrice, bandTop),
      rate: effectiveRate,
      duty,
    })
    totalDuty += duty
  }

  // If additional/non-uk, the surcharge also applies to bands that had 0% base rate
  // This is already handled above since we add surcharge to every band

  const effectiveRate =
    propertyPrice > 0 ? (totalDuty / propertyPrice) * 100 : 0

  return {
    totalDuty: Math.round(totalDuty),
    effectiveRate: Math.round(effectiveRate * 100) / 100,
    bands,
    propertyPrice,
    buyerType,
  }
}

export { formatCurrency, formatPercent } from "@/lib/format-currency"
